// src/screens/Dashboard.jsx
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import ChartCard from "../components/ChartCard";
import MetricsCards from "../components/MetricsCards";
import useIdleLogout from "../hooks/useIdleLogout";

import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title as ChartTitle,
  Tooltip,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  ChartTitle,
  Tooltip,
  Legend
);

/* ================= CONFIG (API URLs) ================= */
const FOOD_URL = (process.env.REACT_APP_FOOD_ORDERS_API_URL || "").trim();
const MART_URL = (process.env.REACT_APP_MART_ORDERS_API_URL || "").trim();
const TRANSPORT_URL = (process.env.REACT_APP_RIDES_API_URL || "").trim();

/* ✅ NEW SERVICES */
const ENTERTAINMENT_URL = (process.env.REACT_APP_ENTERTAINMENT_ORDERS_API_URL || "").trim();
const EVENTS_DASH_URL   = (process.env.REACT_APP_EVENTS_API_URL || "").replace(/\/+$/, "");
const DINING_URL = (process.env.REACT_APP_DINING_ORDERS_API_URL || "").trim();
const HOTEL_URL = (process.env.REACT_APP_HOTEL_ORDERS_API_URL || "").trim();

/* ================= AUTH HELPERS ================= */
function safeGetStorage(storage, key) {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function readStoredUser() {
  try {
    const raw =
      safeGetStorage(localStorage, "user") ||
      safeGetStorage(localStorage, "admin") ||
      safeGetStorage(localStorage, "profile") ||
      safeGetStorage(localStorage, "currentUser");

    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function getBearerToken() {
  const directToken =
    safeGetStorage(localStorage, "access_token") ||
    safeGetStorage(localStorage, "token") ||
    safeGetStorage(localStorage, "adminToken") ||
    safeGetStorage(localStorage, "accessToken") ||
    safeGetStorage(localStorage, "authToken") ||
    safeGetStorage(sessionStorage, "access_token") ||
    safeGetStorage(sessionStorage, "token") ||
    safeGetStorage(sessionStorage, "adminToken") ||
    safeGetStorage(sessionStorage, "accessToken") ||
    safeGetStorage(sessionStorage, "authToken");

  if (directToken) return String(directToken).trim();

  const user = readStoredUser();

  return String(
    user?.token ||
    user?.adminToken ||
    user?.accessToken ||
    user?.access_token ||
    user?.authToken ||
    user?.jwt ||
    ""
  ).trim();
}

function getAuthHeaders() {
  const token = getBearerToken();

  const headers = {
    Accept: "application/json",
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

/* Optional: only used for the "Open vs Closed" donuts */
const MERCHANTS_URL = (process.env.REACT_APP_MERCHANTS_API_URL || "").trim();

/* ================= GENERAL HELPERS ================= */
function normalizeRows(rows = []) {
  return rows.map((r, i) => ({
    id: i + 1,
    orderId: r.Order_ID ?? r.order_id ?? r.orderId,
    customerName: r.Customer_Name ?? r.customer_name ?? r.customer,
    businessName:
      r.Business_Name ?? r.business_name ?? r.restaurant ?? r.business,
    itemsName: r.Items_Name ?? r.items_name ?? r.itemsName ?? "",
    totalQty: Number(r.Total_Quantity ?? r.total_quantity ?? r.itemsCount ?? 0),
    totalAmount: Number(r.Total_Amount ?? r.total_amount ?? r.amount ?? 0),
    payment: r.Payment ?? r.payment,
    status: r.Status ?? r.status,
    placedAt:
      r.Placed_At ??
      r.placed_at ??
      r.placedAt ??
      r.date ??
      r.order_date ??
      r.created_at ??
      r.createdAt ??
      null,
  }));
}

/* ---------- payouts-aware transport normalizer (platform revenue) ---------- */
function normalizeTransportData(rides = []) {
  return rides.map((ride, i) => {
    if (ride?.driver_details && ride?.ride_details && ride?.fare_details) {
      const dd = ride.driver_details || {};
      const pd = ride.passenger_details || {};
      const rd = ride.ride_details || {};
      const fd = ride.fare_details || {};

      const completedAt = rd.completed_at || null;
      const startedAt = rd.started_at || null;
      const acceptedAt = rd.accepted_at || null;
      const createdAt =
        completedAt || startedAt || acceptedAt || rd.requested_at || null;

      return {
        id: rd.ride_id || i + 1,
        rideId: rd.ride_id || `ride-${i + 1}`,
        driverId: dd.driver_id,
        driverName: (dd.driver_name || "").trim() || "Unknown Driver",
        driverPhone: dd.driver_phone,
        passengerId: pd.passenger_id,
        passengerName: (pd.passenger_name || "").trim() || "Unknown Passenger",
        passengerPhone: pd.passenger_phone,
        pickupLocation: rd.pickup_place || "Unknown",
        dropoffLocation: rd.dropoff_place || "Unknown",
        status: completedAt ? "completed" : ride.status || "in_progress",
        platformFee: Number(fd.platform_fee_nu ?? 0),
        tax: Number(fd.tax_nu ?? 0),
        platformFeeWithTax:
          Number(fd.platform_fee_nu ?? 0) + Number(fd.tax_nu ?? 0),
        fare:
          Number(fd.driver_take_home_nu ?? 0) +
          Number(fd.time_gross_nu ?? 0) +
          Number(fd.tips_nu ?? 0),
        distance:
          typeof rd.distance_m === "number"
            ? rd.distance_m / 1000
            : Number(rd.distance_m || 0) / 1000,
        duration: Number(rd.duration_s || 0),
        currency: fd.currency || "BTN",
        rideType: rd.service_type || rd.trip_type || "",
        tripType: rd.trip_type || "",
        payment: fd.payment_method || "",
        createdAt,
        acceptedAt,
        arrivedPickupAt: rd.arrived_pickup_at || null,
        startedAt,
        completedAt,
      };
    }

    const earnings = ride.earnings || {};
    return {
      id: ride.ride_id || i + 1,
      rideId: ride.ride_id || `ride-${i + 1}`,
      driverId: ride.driver_id,
      driverName: ride.driver_name || "Unknown Driver",
      driverPhone: ride.driver_phone,
      passengerId: ride.passenger_id,
      passengerName: ride.passenger_name || "Unknown Passenger",
      passengerPhone: ride.passenger_phone,
      pickupLocation: ride.pickup_place || "Unknown",
      dropoffLocation: ride.dropoff_place || "Unknown",
      status: ride.status || "unknown",
      platformFee: (earnings.platform_fee_cents || 0) / 100,
      tax: (earnings.tax_cents || 0) / 100,
      platformFeeWithTax:
        (earnings.platform_fee_cents || 0) / 100 +
        (earnings.tax_cents || 0) / 100,
      fare: 0,
      distance: (ride.distance_m || 0) / 1000,
      duration: ride.duration_s || 0,
      currency: ride.currency || "BTN",
      rideType: ride.ride_type,
      tripType: ride.trip_type,
      payment: ride.payment_method || ride.payment || "",
      createdAt:
        ride.created_at ??
        ride.createdAt ??
        ride.placed_at ??
        ride.started_at ??
        null,
      acceptedAt: ride.accepted_at,
      arrivedPickupAt: ride.arrived_pickup_at,
      startedAt: ride.started_at,
      completedAt: ride.completed_at,
    };
  });
}

/* ---------- events API → dashboard row normalizer ---------- */
function normalizeEventsForDashboard(events = []) {
  return events.map((ev) => ({
    orderId:      ev.id,
    businessName: ev.category
      ? ev.category.charAt(0).toUpperCase() + ev.category.slice(1)
      : "Event",
    customerName: ev.organizer_name || "",
    itemsName:    ev.title || "",
    totalQty:     Number(ev.tickets_sold  || 0),
    totalAmount:  Number(ev.gross_revenue || 0),
    payment:      "Wallet",
    status:       (Number(ev.booking_count || 0) > 0 || Number(ev.gross_revenue || 0) > 0)
                    ? "completed"
                    : "pending",
    placedAt:     ev.start_at || null,
  }));
}

const fmtAmt = (n) =>
  `BTN. ${Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/* ========= compact ticks & autoscale ========= */
const fmtCompact = (n) =>
  new Intl.NumberFormat("en", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(n ?? 0);

const fmtNuCompact = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "BTN",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(n ?? 0);

function yAxisNumberOptions({ currency = false, data = [] } = {}) {
  const max = Math.max(0, ...data);
  const suggestedMax = max > 0 ? max * 1.1 : undefined;

  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: true },
      tooltip: {
        enabled: true,
        intersect: false,
        callbacks: {
          label: (ctx) => {
            const v = ctx.parsed?.y ?? ctx.parsed;
            return currency
              ? `BTN. ${Number(v ?? 0).toLocaleString("en-IN")}`
              : `${Number(v ?? 0).toLocaleString("en-IN")}`;
          },
          title: (items) => items?.[0]?.label ?? "",
        },
      },
    },
    scales: {
      x: { ticks: { autoSkip: true, maxRotation: 0 }, grid: { display: false } },
      y: {
        beginAtZero: true,
        grace: "10%",
        suggestedMax,
        ticks: {
          maxTicksLimit: 6,
          callback: (value) =>
            currency ? fmtNuCompact(value) : fmtCompact(value),
        },
      },
    },
  };
}

/* ---- date parsing helper ---- */
const parseDate = (dateString) => {
  if (!dateString) return null;
  const formats = [
    () => new Date(dateString),
    () => new Date(String(dateString).replace(" ", "T")),
    () => new Date(String(dateString).replace(" ", "T") + "Z"),
    () => new Date(String(dateString).split(" ")[0]),
    () => {
      const parts = String(dateString).split(/[-/ :]/);
      if (parts.length >= 3) return new Date(parts[0], parts[1] - 1, parts[2]);
      return new Date(NaN);
    },
  ];
  for (const f of formats) {
    try {
      const d = f();
      if (!isNaN(d.getTime())) return d;
    } catch { }
  }
  return null;
};

/* ================== TIME / WEEK HELPERS (Asia/Thimphu) ================== */
const TZ = "Asia/Thimphu";
const WEEK_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function minutesInTZ(timeZone) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date());
  const h = Number(parts.find((p) => p.type === "hour").value);
  const m = Number(parts.find((p) => p.type === "minute").value);
  return h * 60 + m;
}
function timeLabelInTZ(timeZone) {
  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date());
}
function toTZKey(date, tz = TZ) {
  const d = date instanceof Date ? date : parseDate(date);
  if (!d) return null;
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  const y = parts.find((p) => p.type === "year")?.value || "0000";
  const m = parts.find((p) => p.type === "month")?.value || "01";
  const day = parts.find((p) => p.type === "day")?.value || "01";
  return `${y}-${m}-${day}`;
}
function weekdayShort(date, tz = TZ) {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    weekday: "short",
  }).format(date);
}
function getWeekKeys(tz = TZ, which = "this") {
  const today = new Date();
  const wd = weekdayShort(today, tz);
  const sunIdx = DAYS_SHORT.indexOf(wd);
  const daysSinceMonday = (sunIdx + 6) % 7;
  const startOffset = daysSinceMonday + (which === "prev" ? 7 : 0);

  const keys = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() - startOffset + i);
    keys.push(toTZKey(d, tz));
  }
  return keys;
}

/* ---- open/close helpers ---- */
function parseHMSToMinutes(hms) {
  const m = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(String(hms || "").trim());
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}
function isOpenNow(openStr, closeStr, nowMin) {
  const open = parseHMSToMinutes(openStr);
  const close = parseHMSToMinutes(closeStr);
  if (open == null || close == null) return false;
  if (open === close) return true;
  if (close > open) return nowMin >= open && nowMin < close;
  return nowMin >= open || nowMin < close;
}

/* ✅ FIX for eslint: stable helper OUTSIDE component */
function calcOpenClosed(list, nowMinBT) {
  const safe = Array.isArray(list) ? list : [];
  const open = safe.reduce(
    (acc, m) =>
      acc + (isOpenNow(m.opening_time, m.closing_time, nowMinBT) ? 1 : 0),
    0
  );
  return { open, closed: safe.length - open };
}

/* ---- payment normalization ---- */
const normalizePayment = (raw) => {
  const s = String(raw || "").trim().toLowerCase();
  const MAP = [
    { label: "Cash on Delivery", match: ["cod", "cash on delivery", "cash"] },
    {
      label: "QR Payment",
      match: [
        "qr",
        "bhutan qr",
        "qr bhutan",
        "ncs qr",
        "scan & pay",
        "scan pay",
        "qr code",
      ],
    },
    { label: "Card", match: ["card", "visa", "mastercard", "debit", "credit"] },
    {
      label: "Bank Transfer",
      match: [
        "bank",
        "bank transfer",
        "ibanking",
        "netbanking",
        "bob",
        "mbob",
        "bank of bhutan",
        "bnb",
        "bhutan national bank",
        "tbank",
        "t bank",
        "t-pay",
        "tpay",
        "mpay",
        "druk",
        "druk pnbl",
        "pnbl",
      ],
    },
    {
      label: "In-App Wallet",
      match: ["wallet", "superapp wallet", "grabpay", "points"],
    },
  ];
  for (const { label, match } of MAP) {
    if (match.some((m) => s === m || s.includes(m))) return label;
  }
  return raw
    ? raw.toString().replace(/\b\w/g, (c) => c.toUpperCase())
    : "Unknown";
};

/* ========= metrics & weekly helpers ========= */
const sum = (arr) => arr.reduce((a, b) => a + b, 0);

function calcMetrics(rows) {
  const totalOrders = rows.length;
  const uniqBusinesses = new Set(
    rows.map((r) => r.businessName).filter(Boolean)
  ).size;
  const revenue = sum(rows.map((r) => r.totalAmount));
  const pending = rows.filter((r) =>
    String(r.status).toUpperCase().includes("PENDING")
  ).length;
  return { totalOrders, uniqBusinesses, revenue, pending };
}

function aggregateWeekCounts(rows, dateField, which = "this") {
  const keys = getWeekKeys(TZ, which);
  const idxByKey = new Map(keys.map((k, i) => [k, i]));
  const counts = Array(7).fill(0);
  for (const r of rows) {
    const d = parseDate(r[dateField]);
    const k = d ? toTZKey(d, TZ) : null;
    if (!k) continue;
    const idx = idxByKey.get(k);
    if (idx != null) counts[idx] += 1;
  }
  return counts;
}

function aggregateWeekSum(rows, dateField, valueField, which = "this") {
  const keys = getWeekKeys(TZ, which);
  const idxByKey = new Map(keys.map((k, i) => [k, i]));
  const sums = Array(7).fill(0);
  for (const r of rows) {
    const d = parseDate(r[dateField]);
    const k = d ? toTZKey(d, TZ) : null;
    if (!k) continue;
    const idx = idxByKey.get(k);
    if (idx != null) sums[idx] += Number(r[valueField] || 0);
  }
  return sums;
}

function aggregateLastNWeeksByWeekday(
  rows,
  dateField,
  valueExtractor,
  nWeeks = 4,
  mode = "avg"
) {
  const mondayOfThis = getWeekKeys(TZ, "this")[0];
  const mondayDate = new Date(mondayOfThis);
  const weekKeys = [];
  for (let i = 0; i < nWeeks; i++) {
    const d = new Date(mondayDate);
    d.setDate(d.getDate() - 7 * i);
    const wk = [];
    for (let j = 0; j < 7; j++) {
      const day = new Date(d);
      day.setDate(d.getDate() + j);
      wk.push(toTZKey(day, TZ));
    }
    weekKeys.push(wk);
  }

  const totals = Array(7).fill(0);
  const countsForAvg = Array(7).fill(0);

  for (const r of rows) {
    const d = parseDate(r[dateField]);
    if (!d) continue;
    const k = toTZKey(d, TZ);
    for (const wk of weekKeys) {
      const idx = wk.indexOf(k);
      if (idx !== -1) {
        const val =
          Number(
            typeof valueExtractor === "function"
              ? valueExtractor(r)
              : r[valueExtractor] || 0
          ) || 0;
        totals[idx] += val;
        countsForAvg[idx] += 1;
        break;
      }
    }
  }

  if (mode === "avg")
    return totals.map((t, i) => (countsForAvg[i] ? t / countsForAvg[i] : 0));
  return totals;
}

function aggregateRangeByWeekday(
  rows,
  dateField,
  valueExtractor,
  startISO,
  endISO,
  mode = "sum"
) {
  if (!startISO || !endISO) return Array(7).fill(0);
  const start = new Date(startISO + "T00:00:00");
  const end = new Date(endISO + "T23:59:59");
  const totals = Array(7).fill(0);
  const counts = Array(7).fill(0);

  for (const r of rows) {
    const d = parseDate(r[dateField]);
    if (!d) continue;
    if (d < start || d > end) continue;
    let idx = d.getDay();
    idx = (idx + 6) % 7; // Mon=0..Sun=6
    const val =
      Number(
        typeof valueExtractor === "function"
          ? valueExtractor(r)
          : r[valueExtractor] || 0
      ) || 0;
    totals[idx] += val;
    counts[idx] += 1;
  }

  if (mode === "avg")
    return totals.map((t, i) => (counts[i] ? t / counts[i] : 0));
  return totals;
}

function weeklySeriesSelector(rows, dateField, period, range, opts = {}) {
  const { mode = "count", valueField } = opts;
  if (period === "this" || period === "prev") {
    return mode === "sum"
      ? aggregateWeekSum(rows, dateField, valueField, period)
      : aggregateWeekCounts(rows, dateField, period);
  }
  if (period === "last4") {
    if (mode === "sum")
      return aggregateLastNWeeksByWeekday(
        rows,
        dateField,
        (r) => r[valueField],
        4,
        "avg"
      );
    return aggregateLastNWeeksByWeekday(rows, dateField, () => 1, 4, "avg");
  }
  if (period === "custom") {
    if (mode === "sum")
      return aggregateRangeByWeekday(
        rows,
        dateField,
        (r) => r[valueField],
        range.start,
        range.end,
        "sum"
      );
    return aggregateRangeByWeekday(
      rows,
      dateField,
      () => 1,
      range.start,
      range.end,
      "sum"
    );
  }
  return Array(7).fill(0);
}

const isOrderDelivered = (s) => {
  const x = String(s || "").trim().toLowerCase();
  return x === "delivered" || x === "completed";
};
const isRideCompleted = (s, completedAt) => {
  if (completedAt) return true;
  const x = String(s || "").trim().toLowerCase();
  return x === "completed" || x === "complete" || x === "done";
};

function filterRowsByPeriod(rows, dateField, period, range) {
  if (!Array.isArray(rows) || !rows.length) return [];

  if (period === "last4") {
    const mondayThisISO = getWeekKeys(TZ, "this")[0];
    const start = new Date(mondayThisISO + "T00:00:00");
    start.setDate(start.getDate() - 28);
    const end = new Date();
    return rows.filter((r) => {
      const d = parseDate(r[dateField]);
      return d && d >= start && d <= end;
    });
  }

  if (period === "custom") {
    if (!range?.start || !range?.end) return [];
    const start = new Date(range.start + "T00:00:00");
    const end = new Date(range.end + "T23:59:59");
    return rows.filter((r) => {
      const d = parseDate(r[dateField]);
      return d && d >= start && d <= end;
    });
  }

  const keys = getWeekKeys(TZ, period);
  const keySet = new Set(keys);
  return rows.filter((r) => {
    const d = parseDate(r[dateField]);
    const k = d ? toTZKey(d, TZ) : null;
    return k && keySet.has(k);
  });
}

/* ================= DASHBOARD ================= */
export default function Dashboard() {
  useIdleLogout();

  /* ✅ Scroll lock helper: prevents jumping to Food/Top on select change */
  const runWithoutScrollJump = useCallback((fn) => {
    if (typeof window === "undefined") return fn();
    const y = window.scrollY || 0;
    fn();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo({ top: y, left: 0, behavior: "auto" });
      });
    });
  }, []);

  const wrapSetter = useCallback(
    (setter) => (value) => runWithoutScrollJump(() => setter(value)),
    [runWithoutScrollJump]
  );

  /* ✅ NEW: strong scroll-guard for native select/date pickers (Dining+below) */
  const guardActiveRef = useRef(false);
  const guardYRef = useRef(0);
  const guardTimerRef = useRef(null);

  const stopScrollGuard = useCallback(() => {
    guardActiveRef.current = false;
    if (guardTimerRef.current) {
      clearTimeout(guardTimerRef.current);
      guardTimerRef.current = null;
    }
  }, []);

  const startScrollGuard = useCallback(() => {
    if (typeof window === "undefined") return;
    guardYRef.current = window.scrollY || 0;
    guardActiveRef.current = true;

    if (guardTimerRef.current) clearTimeout(guardTimerRef.current);
    guardTimerRef.current = setTimeout(() => {
      guardActiveRef.current = false;
      guardTimerRef.current = null;
    }, 650);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo({ top: guardYRef.current, left: 0, behavior: "auto" });
      });
    });
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const onScroll = () => {
      if (!guardActiveRef.current) return;
      const y = guardYRef.current || 0;
      if ((window.scrollY || 0) !== y) {
        window.scrollTo({ top: y, left: 0, behavior: "auto" });
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* ✅ Disable scroll anchoring at document level while on dashboard */
  useEffect(() => {
    if (typeof document === "undefined") return;
    const html = document.documentElement;
    const body = document.body;
    const prevHtml = html.style.overflowAnchor;
    const prevBody = body.style.overflowAnchor;
    html.style.overflowAnchor = "none";
    body.style.overflowAnchor = "none";
    return () => {
      html.style.overflowAnchor = prevHtml || "";
      body.style.overflowAnchor = prevBody || "";
    };
  }, []);

  /* ✅ cleanup any pending guard timer on unmount */
  useEffect(() => {
    return () => {
      if (guardTimerRef.current) {
        clearTimeout(guardTimerRef.current);
        guardTimerRef.current = null;
      }
    };
  }, []);

  /* ---------- Chart.js theme sync ---------- */
  useEffect(() => {
    const applyTheme = () => {
      if (typeof document === "undefined") return;
      const styles = getComputedStyle(document.documentElement);
      ChartJS.defaults.color =
        styles.getPropertyValue("--text-color").trim() || "#212529";
      ChartJS.defaults.font.family =
        styles.getPropertyValue("--font-family").trim() || "inherit";
      ChartJS.defaults.elements.point.radius = 3;
      ChartJS.defaults.elements.point.hoverRadius = 6;
      ChartJS.defaults.elements.point.hitRadius = 14;
      ChartJS.defaults.plugins.tooltip.enabled = true;
      ChartJS.defaults.plugins.tooltip.intersect = false;
    };
    applyTheme();
    if (
      typeof MutationObserver === "undefined" ||
      typeof document === "undefined"
    )
      return;

    const obs = new MutationObserver(applyTheme);
    obs.observe(document.body, { attributes: true });
    return () => obs.disconnect();
  }, []);

  /* ---------- Fetch Food & Mart & Transport + NEW services ---------- */
  const [foodRows, setFoodRows] = useState([]);
  const [martRows, setMartRows] = useState([]);
  const [transportRows, setTransportRows] = useState([]);

  const [entRows, setEntRows] = useState([]);
  const [diningRows, setDiningRows] = useState([]);
  const [hotelRows, setHotelRows] = useState([]);
  const [eventsRaw, setEventsRaw] = useState([]);

  const [foodErr, setFoodErr] = useState("");
  const [martErr, setMartErr] = useState("");
  const [transportErr, setTransportErr] = useState("");

  const [entErr, setEntErr] = useState("");
  const [diningErr, setDiningErr] = useState("");
  const [hotelErr, setHotelErr] = useState("");

  useEffect(() => {
    let alive = true;

    const fetchOrders = async (url, setRows, setErr, label = "API") => {
      try {
        setErr("");
        if (!url) {
          if (alive) setRows([]);
          return;
        }
        const res = await fetch(url, {
          method: "GET",
          headers: getAuthHeaders(),
          cache: "no-store",
        });
        if (!res.ok) throw new Error(`${label} HTTP ${res.status}`);
        const json = await res.json();
        const arr = Array.isArray(json?.data)
          ? json.data
          : Array.isArray(json)
            ? json
            : [];
        if (alive) setRows(normalizeRows(arr));
      } catch (e) {
        alive && setErr(`Failed to load: ${e.message}`);
      }
    };

    const fetchTransport = async () => {
      try {
        setTransportErr("");
        if (!TRANSPORT_URL) {
          if (alive) setTransportRows([]);
          return;
        }
        const res = await fetch(TRANSPORT_URL, {
          method: "GET",
          headers: getAuthHeaders(),
          cache: "no-store",
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        let rides = [];
        if (Array.isArray(json)) rides = json;
        else if (Array.isArray(json?.data)) rides = json.data;
        else if (Array.isArray(json?.rides)) rides = json.rides;
        else if (Array.isArray(json?.results)) rides = json.results;
        else if (Array.isArray(json?.items)) rides = json.items;
        else if (Array.isArray(json?.data?.items)) rides = json.data.items;
        if (alive) setTransportRows(normalizeTransportData(rides));
      } catch (e) {
        alive && setTransportErr(`Failed to load transport: ${e.message}`);
      }
    };

    fetchOrders(FOOD_URL, setFoodRows, setFoodErr, "Food");
    fetchOrders(MART_URL, setMartRows, setMartErr, "Mart");
    fetchTransport();

    // Fetch events from Events API and merge into entertainment rows
    const fetchEvents = async () => {
      if (!EVENTS_DASH_URL) {
        fetchOrders(ENTERTAINMENT_URL, setEntRows, setEntErr, "Entertainment");
        return;
      }
      try {
        setEntErr("");
        const url = `${EVENTS_DASH_URL}/events?limit=100&page=1`;
        const res = await fetch(url, { method: "GET", headers: getAuthHeaders(), cache: "no-store" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const arr = Array.isArray(json?.data) ? json.data : Array.isArray(json) ? json : [];
        if (alive) {
          setEventsRaw(arr);
          setEntRows(normalizeEventsForDashboard(arr));
        }
      } catch (e) {
        alive && setEntErr(`Failed to load events: ${e.message}`);
        fetchOrders(ENTERTAINMENT_URL, setEntRows, setEntErr, "Entertainment");
      }
    };

    fetchEvents();
    fetchOrders(DINING_URL, setDiningRows, setDiningErr, "Dining Out");
    fetchOrders(HOTEL_URL, setHotelRows, setHotelErr, "Hotel");

    return () => {
      alive = false;
    };
  }, []);

  /* ---------- Merchants for Open/Closed ---------- */
  const [merchants, setMerchants] = useState([]);
  const [merchErr, setMerchErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!MERCHANTS_URL) {
        setMerchErr("");
        if (alive) setMerchants([]);
        return;
      }
      try {
        setMerchErr("");
        const res = await fetch(MERCHANTS_URL, {
          method: "GET",
          headers: getAuthHeaders(),
          cache: "no-store",
        });
        // 404 / 403 means this role has no merchant access — treat as empty, not an error
        if (res.status === 404 || res.status === 403) {
          if (alive) setMerchants([]);
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const payload = await res.json();
        const arr = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
            ? payload.data
            : [];
        const norm = arr.map((m) => ({
          ...m,
          owner_type: String(m.owner_type || "").toLowerCase(),
        }));
        if (alive) setMerchants(norm);
      } catch (e) {
        // Merchant data is only used for Open/Closed doughnuts — log silently, don't display
        console.warn("[Dashboard] Merchants unavailable:", e.message);
        if (alive) setMerchants([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  /* ================== WEEK / RANGE STATE ================== */
  const [transportWeek, setTransportWeek] = useState("this");
  const [foodWeek, setFoodWeek] = useState("this");
  const [martWeek, setMartWeek] = useState("this");

  const [entWeek, setEntWeek] = useState("this");
  const [diningWeek, setDiningWeek] = useState("this");
  const [hotelWeek, setHotelWeek] = useState("this");

  const [transportRange, setTransportRange] = useState({ start: "", end: "" });
  const [foodRange, setFoodRange] = useState({ start: "", end: "" });
  const [martRange, setMartRange] = useState({ start: "", end: "" });

  const [entRange, setEntRange] = useState({ start: "", end: "" });
  const [diningRange, setDiningRange] = useState({ start: "", end: "" });
  const [hotelRange, setHotelRange] = useState({ start: "", end: "" });

  /* ✅ Wrapped setters = NO SCROLL JUMP */
  const setTransportWeekSafe = useMemo(
    () => wrapSetter(setTransportWeek),
    [wrapSetter]
  );
  const setFoodWeekSafe = useMemo(() => wrapSetter(setFoodWeek), [wrapSetter]);
  const setMartWeekSafe = useMemo(() => wrapSetter(setMartWeek), [wrapSetter]);
  const setEntWeekSafe = useMemo(() => wrapSetter(setEntWeek), [wrapSetter]);
  const setDiningWeekSafe = useMemo(
    () => wrapSetter(setDiningWeek),
    [wrapSetter]
  );
  const setHotelWeekSafe = useMemo(
    () => wrapSetter(setHotelWeek),
    [wrapSetter]
  );

  const setTransportRangeSafe = useMemo(
    () => wrapSetter(setTransportRange),
    [wrapSetter]
  );
  const setFoodRangeSafe = useMemo(() => wrapSetter(setFoodRange), [wrapSetter]);
  const setMartRangeSafe = useMemo(() => wrapSetter(setMartRange), [wrapSetter]);
  const setEntRangeSafe = useMemo(() => wrapSetter(setEntRange), [wrapSetter]);
  const setDiningRangeSafe = useMemo(
    () => wrapSetter(setDiningRange),
    [wrapSetter]
  );
  const setHotelRangeSafe = useMemo(
    () => wrapSetter(setHotelRange),
    [wrapSetter]
  );

  /* ---------- Open vs Closed (Asia/Thimphu) ---------- */
  const nowMinBT = minutesInTZ(TZ);
  const nowLabel = timeLabelInTZ(TZ);

  const foodMerchants = useMemo(
    () => merchants.filter((m) => m.owner_type === "food"),
    [merchants]
  );
  const martMerchants = useMemo(
    () => merchants.filter((m) => m.owner_type === "mart"),
    [merchants]
  );
  const entMerchants = useMemo(
    () => merchants.filter((m) => m.owner_type === "entertainment"),
    [merchants]
  );
  const diningMerchants = useMemo(
    () => merchants.filter((m) => m.owner_type === "dining"),
    [merchants]
  );
  const hotelMerchants = useMemo(
    () => merchants.filter((m) => m.owner_type === "hotel"),
    [merchants]
  );

  const foodOpenClosed = useMemo(
    () => calcOpenClosed(foodMerchants, nowMinBT),
    [foodMerchants, nowMinBT]
  );
  const martOpenClosed = useMemo(
    () => calcOpenClosed(martMerchants, nowMinBT),
    [martMerchants, nowMinBT]
  );
  const entOpenClosed = useMemo(
    () => calcOpenClosed(entMerchants, nowMinBT),
    [entMerchants, nowMinBT]
  );
  const diningOpenClosed = useMemo(
    () => calcOpenClosed(diningMerchants, nowMinBT),
    [diningMerchants, nowMinBT]
  );
  const hotelOpenClosed = useMemo(
    () => calcOpenClosed(hotelMerchants, nowMinBT),
    [hotelMerchants, nowMinBT]
  );

  /* ================== PERIOD-FILTERED ROWS FOR METRIC CARDS ================== */
  const foodRowsPeriod = useMemo(
    () => filterRowsByPeriod(foodRows, "placedAt", foodWeek, foodRange),
    [foodRows, foodWeek, foodRange]
  );
  const foodCompletedPeriod = useMemo(
    () => foodRowsPeriod.filter((r) => isOrderDelivered(r.status)),
    [foodRowsPeriod]
  );

  const martRowsPeriod = useMemo(
    () => filterRowsByPeriod(martRows, "placedAt", martWeek, martRange),
    [martRows, martWeek, martRange]
  );
  const martCompletedPeriod = useMemo(
    () => martRowsPeriod.filter((r) => isOrderDelivered(r.status)),
    [martRowsPeriod]
  );

  const transportRowsPeriod = useMemo(
    () =>
      filterRowsByPeriod(
        transportRows,
        "createdAt",
        transportWeek,
        transportRange
      ),
    [transportRows, transportWeek, transportRange]
  );
  const transportCompletedPeriod = useMemo(
    () =>
      transportRowsPeriod.filter((r) =>
        isRideCompleted(r.status, r.completedAt)
      ),
    [transportRowsPeriod]
  );

  const entRowsPeriod = useMemo(
    () => filterRowsByPeriod(entRows, "placedAt", entWeek, entRange),
    [entRows, entWeek, entRange]
  );
  const entCompletedPeriod = useMemo(
    () => entRowsPeriod.filter((r) => isOrderDelivered(r.status)),
    [entRowsPeriod]
  );

  const diningRowsPeriod = useMemo(
    () => filterRowsByPeriod(diningRows, "placedAt", diningWeek, diningRange),
    [diningRows, diningWeek, diningRange]
  );
  const diningCompletedPeriod = useMemo(
    () => diningRowsPeriod.filter((r) => isOrderDelivered(r.status)),
    [diningRowsPeriod]
  );

  const hotelRowsPeriod = useMemo(
    () => filterRowsByPeriod(hotelRows, "placedAt", hotelWeek, hotelRange),
    [hotelRows, hotelWeek, hotelRange]
  );
  const hotelCompletedPeriod = useMemo(
    () => hotelRowsPeriod.filter((r) => isOrderDelivered(r.status)),
    [hotelRowsPeriod]
  );

  /* ---------- Metric values (period-based) ---------- */
  const foodM = useMemo(() => calcMetrics(foodRowsPeriod), [foodRowsPeriod]);
  const martM = useMemo(() => calcMetrics(martRowsPeriod), [martRowsPeriod]);
  const entM = useMemo(() => calcMetrics(entRowsPeriod), [entRowsPeriod]);
  const diningM = useMemo(
    () => calcMetrics(diningRowsPeriod),
    [diningRowsPeriod]
  );
  const hotelM = useMemo(() => calcMetrics(hotelRowsPeriod), [hotelRowsPeriod]);

  const foodRevenueCompleted = useMemo(
    () => sum(foodCompletedPeriod.map((r) => r.totalAmount || 0)),
    [foodCompletedPeriod]
  );
  const martRevenueCompleted = useMemo(
    () => sum(martCompletedPeriod.map((r) => r.totalAmount || 0)),
    [martCompletedPeriod]
  );
  const entRevenueCompleted = useMemo(
    () => sum(entCompletedPeriod.map((r) => r.totalAmount || 0)),
    [entCompletedPeriod]
  );
  const diningRevenueCompleted = useMemo(
    () => sum(diningCompletedPeriod.map((r) => r.totalAmount || 0)),
    [diningCompletedPeriod]
  );
  const hotelRevenueCompleted = useMemo(
    () => sum(hotelCompletedPeriod.map((r) => r.totalAmount || 0)),
    [hotelCompletedPeriod]
  );
  const transportRevenueCompleted = useMemo(
    () => sum(transportCompletedPeriod.map((r) => r.platformFeeWithTax || 0)),
    [transportCompletedPeriod]
  );

  const transportCounts = useMemo(() => {
    const completed = transportCompletedPeriod.length;
    const cancelled = transportRowsPeriod.filter((r) =>
      /cancelled/i.test(String(r.status))
    ).length;
    const inProgress = transportRowsPeriod.length - completed - cancelled;
    return {
      completed,
      cancelled,
      inProgress,
      total: transportRowsPeriod.length,
    };
  }, [transportRowsPeriod, transportCompletedPeriod]);

  /* ================== WEEK AGGREGATIONS (charts) ================== */
  const transportRidesPerWeek = useMemo(
    () =>
      weeklySeriesSelector(
        transportRows,
        "createdAt",
        transportWeek,
        transportRange,
        { mode: "count" }
      ),
    [transportRows, transportWeek, transportRange]
  );
  const transportRevenuePerWeek = useMemo(
    () =>
      weeklySeriesSelector(
        transportRows.filter((r) => isRideCompleted(r.status, r.completedAt)),
        "createdAt",
        transportWeek,
        transportRange,
        { mode: "sum", valueField: "platformFeeWithTax" }
      ),
    [transportRows, transportWeek, transportRange]
  );

  const foodOrdersPerWeek = useMemo(
    () =>
      weeklySeriesSelector(foodRows, "placedAt", foodWeek, foodRange, {
        mode: "count",
      }),
    [foodRows, foodWeek, foodRange]
  );
  const foodRevenuePerWeek = useMemo(
    () =>
      weeklySeriesSelector(
        foodRows.filter((r) => isOrderDelivered(r.status)),
        "placedAt",
        foodWeek,
        foodRange,
        { mode: "sum", valueField: "totalAmount" }
      ),
    [foodRows, foodWeek, foodRange]
  );

  const martItemsPerWeek = useMemo(
    () =>
      weeklySeriesSelector(martRows, "placedAt", martWeek, martRange, {
        mode: "sum",
        valueField: "totalQty",
      }),
    [martRows, martWeek, martRange]
  );
  const martRevenuePerWeek = useMemo(
    () =>
      weeklySeriesSelector(
        martRows.filter((r) => isOrderDelivered(r.status)),
        "placedAt",
        martWeek,
        martRange,
        { mode: "sum", valueField: "totalAmount" }
      ),
    [martRows, martWeek, martRange]
  );

  const entOrdersPerWeek = useMemo(
    () =>
      weeklySeriesSelector(entRows, "placedAt", entWeek, entRange, {
        mode: "count",
      }),
    [entRows, entWeek, entRange]
  );
  const entRevenuePerWeek = useMemo(
    () =>
      weeklySeriesSelector(
        entRows.filter((r) => isOrderDelivered(r.status)),
        "placedAt",
        entWeek,
        entRange,
        { mode: "sum", valueField: "totalAmount" }
      ),
    [entRows, entWeek, entRange]
  );

  const diningOrdersPerWeek = useMemo(
    () =>
      weeklySeriesSelector(diningRows, "placedAt", diningWeek, diningRange, {
        mode: "count",
      }),
    [diningRows, diningWeek, diningRange]
  );
  const diningRevenuePerWeek = useMemo(
    () =>
      weeklySeriesSelector(
        diningRows.filter((r) => isOrderDelivered(r.status)),
        "placedAt",
        diningWeek,
        diningRange,
        { mode: "sum", valueField: "totalAmount" }
      ),
    [diningRows, diningWeek, diningRange]
  );

  const hotelOrdersPerWeek = useMemo(
    () =>
      weeklySeriesSelector(hotelRows, "placedAt", hotelWeek, hotelRange, {
        mode: "count",
      }),
    [hotelRows, hotelWeek, hotelRange]
  );
  const hotelRevenuePerWeek = useMemo(
    () =>
      weeklySeriesSelector(
        hotelRows.filter((r) => isOrderDelivered(r.status)),
        "placedAt",
        hotelWeek,
        hotelRange,
        { mode: "sum", valueField: "totalAmount" }
      ),
    [hotelRows, hotelWeek, hotelRange]
  );

  /* ---------- Status breakdowns (all-time) ---------- */
  const statusLabelsOf = (rows) =>
    Array.from(new Set(rows.map((r) => r.status).filter(Boolean)));
  const statusCountsOf = (rows, labels) =>
    labels.map((s) => rows.filter((r) => r.status === s).length);

  const foodStatusLabels = useMemo(() => statusLabelsOf(foodRows), [foodRows]);
  const foodStatusCounts = useMemo(
    () => statusCountsOf(foodRows, foodStatusLabels),
    [foodRows, foodStatusLabels]
  );

  const martStatusLabels = useMemo(() => statusLabelsOf(martRows), [martRows]);
  const martStatusCounts = useMemo(
    () => statusCountsOf(martRows, martStatusLabels),
    [martRows, martStatusLabels]
  );

  const entStatusLabels = useMemo(() => statusLabelsOf(entRows), [entRows]);
  const entStatusCounts = useMemo(
    () => statusCountsOf(entRows, entStatusLabels),
    [entRows, entStatusLabels]
  );

  const diningStatusLabels = useMemo(
    () => statusLabelsOf(diningRows),
    [diningRows]
  );
  const diningStatusCounts = useMemo(
    () => statusCountsOf(diningRows, diningStatusLabels),
    [diningRows, diningStatusLabels]
  );

  const hotelStatusLabels = useMemo(
    () => statusLabelsOf(hotelRows),
    [hotelRows]
  );
  const hotelStatusCounts = useMemo(
    () => statusCountsOf(hotelRows, hotelStatusLabels),
    [hotelRows, hotelStatusLabels]
  );

  const transportStatusLabels = useMemo(() => {
    const statuses = Array.from(
      new Set(transportRows.map((r) => r.status).filter(Boolean))
    );
    return statuses.length > 0
      ? statuses
      : ["completed", "in_progress", "cancelled"];
  }, [transportRows]);

  const transportStatusCounts = useMemo(() => {
    return transportStatusLabels.map(
      (s) =>
        transportRows.filter(
          (r) => String(r.status).toLowerCase() === String(s).toLowerCase()
        ).length
    );
  }, [transportRows, transportStatusLabels]);

  /* ---------- Payment donuts — COMPLETED ONLY (all-time) ---------- */
  const WALLET_ONLY_LABEL = "In-App Wallet";
  const paymentLabelsCompleted = () => [WALLET_ONLY_LABEL];

  const paymentCountsCompleted = (rows, _labels, isCompletedFn) => [
    rows
      .filter((r) => isCompletedFn(r.status, r.completedAt))
      .filter((r) => normalizePayment(r.payment) === WALLET_ONLY_LABEL).length,
  ];

  const foodPaymentLabelsCompleted = useMemo(
    () => paymentLabelsCompleted(foodRows, (s) => isOrderDelivered(s)),
    [foodRows]
  );
  const foodPaymentCountsCompleted = useMemo(
    () =>
      paymentCountsCompleted(
        foodRows,
        foodPaymentLabelsCompleted,
        (s) => isOrderDelivered(s)
      ),
    [foodRows, foodPaymentLabelsCompleted]
  );

  const martPaymentLabelsCompleted = useMemo(
    () => paymentLabelsCompleted(martRows, (s) => isOrderDelivered(s)),
    [martRows]
  );
  const martPaymentCountsCompleted = useMemo(
    () =>
      paymentCountsCompleted(
        martRows,
        martPaymentLabelsCompleted,
        (s) => isOrderDelivered(s)
      ),
    [martRows, martPaymentLabelsCompleted]
  );

  const entPaymentLabelsCompleted = useMemo(
    () => paymentLabelsCompleted(entRows, (s) => isOrderDelivered(s)),
    [entRows]
  );
  const entPaymentCountsCompleted = useMemo(
    () =>
      paymentCountsCompleted(
        entRows,
        entPaymentLabelsCompleted,
        (s) => isOrderDelivered(s)
      ),
    [entRows, entPaymentLabelsCompleted]
  );

  const diningPaymentLabelsCompleted = useMemo(
    () => paymentLabelsCompleted(diningRows, (s) => isOrderDelivered(s)),
    [diningRows]
  );
  const diningPaymentCountsCompleted = useMemo(
    () =>
      paymentCountsCompleted(
        diningRows,
        diningPaymentLabelsCompleted,
        (s) => isOrderDelivered(s)
      ),
    [diningRows, diningPaymentLabelsCompleted]
  );

  const hotelPaymentLabelsCompleted = useMemo(
    () => paymentLabelsCompleted(hotelRows, (s) => isOrderDelivered(s)),
    [hotelRows]
  );
  const hotelPaymentCountsCompleted = useMemo(
    () =>
      paymentCountsCompleted(
        hotelRows,
        hotelPaymentLabelsCompleted,
        (s) => isOrderDelivered(s)
      ),
    [hotelRows, hotelPaymentLabelsCompleted]
  );

  const transportPaymentLabelsCompleted = useMemo(
    () => paymentLabelsCompleted(transportRows, isRideCompleted),
    [transportRows]
  );
  const transportPaymentCountsCompleted = useMemo(
    () =>
      paymentCountsCompleted(
        transportRows,
        transportPaymentLabelsCompleted,
        isRideCompleted
      ),
    [transportRows, transportPaymentLabelsCompleted]
  );

  // Ride types (all-time)
  const rideTypeData = useMemo(() => {
    const types = transportRows.reduce((acc, ride) => {
      const type = ride.rideType || "unknown";
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});
    return { labels: Object.keys(types), data: Object.values(types) };
  }, [transportRows]);

  /* ---------- Metrics cards (period-based) ---------- */
  const foodMetrics = [
    { label: "Total Orders", value: foodM.totalOrders.toLocaleString() },
    {
      label: "Active Business Types",
      value: foodM.uniqBusinesses.toLocaleString(),
    },
    { label: "Food Revenue", value: fmtAmt(foodRevenueCompleted) },
    { label: "Pending Orders", value: foodM.pending.toLocaleString() },
  ];

  const martMetrics = [
    { label: "Total Orders", value: martM.totalOrders.toLocaleString() },
    {
      label: "Active Business Types",
      value: martM.uniqBusinesses.toLocaleString(),
    },
    { label: "Mart Revenue", value: fmtAmt(martRevenueCompleted) },
    { label: "Pending Orders", value: martM.pending.toLocaleString() },
  ];

  const transportMetrics = [
    { label: "Total Rides", value: transportCounts.total.toLocaleString() },
    {
      label: "Accepted Rides",
      value: transportRowsPeriod
        .filter((r) => r.acceptedAt)
        .length.toLocaleString(),
    },
    { label: "Transport Revenue", value: fmtAmt(transportRevenueCompleted) },
    {
      label: "Picked Passengers",
      value: new Set(
        transportRowsPeriod
          .filter(
            (r) =>
              r.startedAt ||
              /STARTED|IN_PROGRESS|ACTIVE|COMPLETED/i.test(String(r.status))
          )
          .map((r) => r.passengerId)
          .filter(Boolean)
      ).size.toLocaleString(),
    },
  ];

  const liveEventsCount = useMemo(
    () => eventsRaw.filter((e) => e.is_live).length,
    [eventsRaw]
  );
  const totalTicketsSold = useMemo(
    () => eventsRaw.reduce((s, e) => s + Number(e.tickets_sold || 0), 0),
    [eventsRaw]
  );

  const entMetrics = [
    { label: "Total Events",      value: entM.totalOrders.toLocaleString() },
    { label: "Live Events",       value: liveEventsCount.toLocaleString() },
    { label: "Tickets Sold",      value: totalTicketsSold.toLocaleString() },
    { label: "Events Revenue",    value: fmtAmt(entRevenueCompleted) },
  ];

  const diningMetrics = [
    { label: "Total Orders", value: diningM.totalOrders.toLocaleString() },
    {
      label: "Active Business Types",
      value: diningM.uniqBusinesses.toLocaleString(),
    },
    { label: "Dining Revenue", value: fmtAmt(diningRevenueCompleted) },
    { label: "Pending Orders", value: diningM.pending.toLocaleString() },
  ];

  const hotelMetrics = [
    { label: "Total Orders", value: hotelM.totalOrders.toLocaleString() },
    {
      label: "Active Business Types",
      value: hotelM.uniqBusinesses.toLocaleString(),
    },
    { label: "Hotel Revenue", value: fmtAmt(hotelRevenueCompleted) },
    { label: "Pending Orders", value: hotelM.pending.toLocaleString() },
  ];

  /* ---------- Period selector ---------- */
  const PeriodSelector = ({ value, onChange, range, onRangeChange }) => (
    <div className="d-flex align-items-center gap-2">
      <select
        className="form-select form-select-sm w-auto"
        value={value}
        onMouseDown={startScrollGuard}
        onTouchStart={startScrollGuard}
        onFocus={startScrollGuard}
        onBlur={stopScrollGuard}
        onChange={(e) => {
          const next = e.target.value;
          e.target.blur(); // ✅ critical: stop browser "scroll into view"
          stopScrollGuard();
          onChange(next);
        }}
      >
        <option value="this">This week</option>
        <option value="prev">Previous week</option>
        <option value="last4">Last 4 weeks (avg)</option>
        <option value="custom">Custom range</option>
      </select>

      {value === "custom" && (
        <div className="d-flex align-items-center gap-2">
          <input
            type="date"
            className="form-control form-control-sm"
            value={range.start}
            onMouseDown={startScrollGuard}
            onTouchStart={startScrollGuard}
            onFocus={startScrollGuard}
            onBlur={stopScrollGuard}
            onChange={(e) => {
              const v = e.target.value;
              e.target.blur(); // ✅ critical
              stopScrollGuard();
              onRangeChange({ ...range, start: v });
            }}
          />
          <span>to</span>
          <input
            type="date"
            className="form-control form-control-sm"
            value={range.end}
            onMouseDown={startScrollGuard}
            onTouchStart={startScrollGuard}
            onFocus={startScrollGuard}
            onBlur={stopScrollGuard}
            onChange={(e) => {
              const v = e.target.value;
              e.target.blur(); // ✅ critical
              stopScrollGuard();
              onRangeChange({ ...range, end: v });
            }}
          />
        </div>
      )}
    </div>
  );

  /* ---------- Reusable section for order-based services ---------- */
  const OrdersService = ({
    title,
    err,
    merchNoteErr,
    metrics,
    weekValue,
    setWeekValue,
    rangeValue,
    setRangeValue,
    lineLabel,
    lineData,
    lineColor,
    openClosed,
    statusLabels,
    statusCounts,
    payLabels,
    payCounts,
    revenueData,
    revenueColor,
  }) => (
    <section className="mb-5">
      <h2 className="mb-3">
        {title}{" "}
        {err && <small className="text-danger ms-2">({err})</small>}
      </h2>

      <div className="row row-cols-1 row-cols-md-2 row-cols-lg-4 g-4 mb-4">
        <MetricsCards metrics={metrics} />
      </div>

      <div className="d-flex align-items-center justify-content-between mb-2">
        <h5 className="m-0">Orders Per Day (Mon–Sun)</h5>
        <PeriodSelector
          value={weekValue}
          onChange={setWeekValue}
          range={rangeValue}
          onRangeChange={setRangeValue}
        />
      </div>

      <div className="row gx-4 gy-4 mb-4">
        <div className="col-md-12">
          <ChartCard
            type="line"
            title=""
            labels={WEEK_LABELS}
            datasets={[
              {
                label: lineLabel,
                data: lineData,
                borderColor: lineColor,
                backgroundColor: "rgba(0, 0, 0, 0.08)",
                pointRadius: 3,
                pointHoverRadius: 6,
                pointHitRadius: 14,
                tension: 0.3,
              },
            ]}
            options={yAxisNumberOptions({ currency: false, data: lineData })}
          />
        </div>
      </div>

      <div className="row gx-4 gy-4 mb-4">
        <div className="col-md-6">
          <ChartCard
            type="doughnut"
            title={`Business Types Open vs Closed (Now ${nowLabel})`}
            labels={["Open", "Closed"]}
            datasets={[
              {
                data: [openClosed.open, openClosed.closed],
                backgroundColor: ["#4caf50", "#f44336"],
              },
            ]}
          />
        </div>
        <div className="col-md-6">
          <ChartCard
            type="doughnut"
            title="Order Status Breakdown"
            labels={statusLabels}
            datasets={[{ data: statusCounts }]}
          />
        </div>
      </div>

      <div className="row gx-4 gy-4 mb-5">
        <div className="col-md-6">
          <ChartCard
            type="doughnut"
            title="Payment Method Breakdown (Completed)"
            labels={payLabels}
            datasets={[{ data: payCounts }]}
          />
        </div>
        <div className="col-md-6">
          <ChartCard
            type="bar"
            title="Revenue (per weekday)"
            labels={WEEK_LABELS}
            datasets={[
              {
                label: "Revenue (Nu)",
                data: revenueData,
                backgroundColor: revenueColor,
              },
            ]}
            options={yAxisNumberOptions({ currency: true, data: revenueData })}
          />
        </div>
      </div>
    </section>
  );

  /* ---------- UI ---------- */
  return (
    <div className="container-fluid px-4" style={{ overflowAnchor: "none" }}>
      {/* ================= TRANSPORT ================= */}
      <section className="mb-5">
        <h2 className="mb-3">
          Transport Overview{" "}
          {transportErr && (
            <small className="text-danger ms-2">({transportErr})</small>
          )}
        </h2>

        <div className="row row-cols-1 row-cols-md-2 row-cols-lg-4 g-4 mb-4">
          <MetricsCards metrics={transportMetrics} />
        </div>

        <div className="d-flex align-items-center justify-content-between mb-2">
          <h5 className="m-0">Rides Per Day (Mon–Sun)</h5>
          <PeriodSelector
            value={transportWeek}
            onChange={setTransportWeekSafe}
            range={transportRange}
            onRangeChange={setTransportRangeSafe}
          />
        </div>

        <div className="row gx-4 gy-4 mb-4">
          <div className="col-md-12">
            <ChartCard
              type="line"
              title=""
              labels={WEEK_LABELS}
              datasets={[
                {
                  label:
                    transportWeek === "this"
                      ? "Rides (This week)"
                      : transportWeek === "prev"
                        ? "Rides (Previous week)"
                        : transportWeek === "last4"
                          ? "Rides (Last 4 weeks avg)"
                          : "Rides (Custom range)",
                  data: transportRidesPerWeek,
                  borderColor: "rgb(75, 192, 192)",
                  backgroundColor: "rgba(75, 192, 192, 0.2)",
                  pointRadius: 3,
                  pointHoverRadius: 6,
                  pointHitRadius: 14,
                  tension: 0.3,
                },
              ]}
              options={yAxisNumberOptions({
                currency: false,
                data: transportRidesPerWeek,
              })}
            />
          </div>
        </div>

        <div className="row gx-4 gy-4 mb-4">
          <div className="col-md-6">
            <ChartCard
              type="doughnut"
              title="Ride Status Breakdown"
              labels={transportStatusLabels.map(
                (s) => s.charAt(0).toUpperCase() + s.slice(1)
              )}
              datasets={[
                {
                  data: transportStatusCounts,
                  backgroundColor: [
                    "#4caf50",
                    "#2196f3",
                    "#f44336",
                    "#ff9800",
                    "#9c27b0",
                    "#607d8b",
                  ].slice(0, transportStatusLabels.length),
                },
              ]}
            />
          </div>
          <div className="col-md-6">
            <ChartCard
              type="doughnut"
              title="Payment Method Breakdown (Completed)"
              labels={transportPaymentLabelsCompleted}
              datasets={[{ data: transportPaymentCountsCompleted }]}
            />
          </div>
        </div>

        <div className="row gx-4 gy-4 mb-5">
          <div className="col-md-6">
            <ChartCard
              type="bar"
              title="Ride Types Distribution"
              labels={rideTypeData.labels.map(
                (l) => l.charAt(0).toUpperCase() + l.slice(1)
              )}
              datasets={[
                {
                  label: "Number of Rides",
                  data: rideTypeData.data,
                  backgroundColor: "rgba(153, 102, 255, 0.7)",
                },
              ]}
              options={yAxisNumberOptions({
                currency: false,
                data: rideTypeData.data,
              })}
            />
          </div>
          <div className="col-md-6">
            <ChartCard
              type="bar"
              title="Revenue (per weekday)"
              labels={WEEK_LABELS}
              datasets={[
                {
                  label: "Revenue (Nu)",
                  data: transportRevenuePerWeek,
                  backgroundColor: "rgba(255, 159, 64, 0.7)",
                },
              ]}
              options={yAxisNumberOptions({
                currency: true,
                data: transportRevenuePerWeek,
              })}
            />
          </div>
        </div>
      </section>

      {/* ================= FOOD ================= */}
      <OrdersService
        title="Food Overview"
        err={foodErr}
        merchNoteErr={merchErr}
        metrics={foodMetrics}
        weekValue={foodWeek}
        setWeekValue={setFoodWeekSafe}
        rangeValue={foodRange}
        setRangeValue={setFoodRangeSafe}
        lineLabel={
          foodWeek === "this"
            ? "Orders (This week)"
            : foodWeek === "prev"
              ? "Orders (Previous week)"
              : foodWeek === "last4"
                ? "Orders (Last 4 weeks avg)"
                : "Orders (Custom range)"
        }
        lineData={foodOrdersPerWeek}
        lineColor={"rgb(255, 99, 132)"}
        openClosed={foodOpenClosed}
        statusLabels={foodStatusLabels}
        statusCounts={foodStatusCounts}
        payLabels={foodPaymentLabelsCompleted}
        payCounts={foodPaymentCountsCompleted}
        revenueData={foodRevenuePerWeek}
        revenueColor={"rgba(54, 162, 235, 0.7)"}
      />

      {/* ================= MART ================= */}
      <OrdersService
        title="Mart Overview"
        err={martErr}
        merchNoteErr={merchErr}
        metrics={martMetrics}
        weekValue={martWeek}
        setWeekValue={setMartWeekSafe}
        rangeValue={martRange}
        setRangeValue={setMartRangeSafe}
        lineLabel={
          martWeek === "this"
            ? "Items (This week)"
            : martWeek === "prev"
              ? "Items (Previous week)"
              : martWeek === "last4"
                ? "Items (Last 4 weeks avg)"
                : "Items (Custom range)"
        }
        lineData={martItemsPerWeek}
        lineColor={"rgb(255, 205, 86)"}
        openClosed={martOpenClosed}
        statusLabels={martStatusLabels}
        statusCounts={martStatusCounts}
        payLabels={martPaymentLabelsCompleted}
        payCounts={martPaymentCountsCompleted}
        revenueData={martRevenuePerWeek}
        revenueColor={"rgba(75, 192, 192, 0.7)"}
      />

      {/* ================= ENTERTAINMENT / EVENTS ================= */}
      <OrdersService
        title="Events & Entertainment Overview"
        err={entErr}
        merchNoteErr={merchErr}
        metrics={entMetrics}
        weekValue={entWeek}
        setWeekValue={setEntWeekSafe}
        rangeValue={entRange}
        setRangeValue={setEntRangeSafe}
        lineLabel={
          entWeek === "this"
            ? "Orders (This week)"
            : entWeek === "prev"
              ? "Orders (Previous week)"
              : entWeek === "last4"
                ? "Orders (Last 4 weeks avg)"
                : "Orders (Custom range)"
        }
        lineData={entOrdersPerWeek}
        lineColor={"rgb(153, 102, 255)"}
        openClosed={entOpenClosed}
        statusLabels={entStatusLabels}
        statusCounts={entStatusCounts}
        payLabels={entPaymentLabelsCompleted}
        payCounts={entPaymentCountsCompleted}
        revenueData={entRevenuePerWeek}
        revenueColor={"rgba(153, 102, 255, 0.7)"}
      />

      {/* ================= DINING OUT ================= */}
      <OrdersService
        title="Dining Out Overview"
        err={diningErr}
        merchNoteErr={merchErr}
        metrics={diningMetrics}
        weekValue={diningWeek}
        setWeekValue={setDiningWeekSafe}
        rangeValue={diningRange}
        setRangeValue={setDiningRangeSafe}
        lineLabel={
          diningWeek === "this"
            ? "Orders (This week)"
            : diningWeek === "prev"
              ? "Orders (Previous week)"
              : diningWeek === "last4"
                ? "Orders (Last 4 weeks avg)"
                : "Orders (Custom range)"
        }
        lineData={diningOrdersPerWeek}
        lineColor={"rgb(255, 159, 64)"}
        openClosed={diningOpenClosed}
        statusLabels={diningStatusLabels}
        statusCounts={diningStatusCounts}
        payLabels={diningPaymentLabelsCompleted}
        payCounts={diningPaymentCountsCompleted}
        revenueData={diningRevenuePerWeek}
        revenueColor={"rgba(255, 159, 64, 0.7)"}
      />

      {/* ================= HOTEL ================= */}
      <OrdersService
        title="Hotel Overview"
        err={hotelErr}
        merchNoteErr={merchErr}
        metrics={hotelMetrics}
        weekValue={hotelWeek}
        setWeekValue={setHotelWeekSafe}
        rangeValue={hotelRange}
        setRangeValue={setHotelRangeSafe}
        lineLabel={
          hotelWeek === "this"
            ? "Orders (This week)"
            : hotelWeek === "prev"
              ? "Orders (Previous week)"
              : hotelWeek === "last4"
                ? "Orders (Last 4 weeks avg)"
                : "Orders (Custom range)"
        }
        lineData={hotelOrdersPerWeek}
        lineColor={"rgb(99, 102, 241)"}
        openClosed={hotelOpenClosed}
        statusLabels={hotelStatusLabels}
        statusCounts={hotelStatusCounts}
        payLabels={hotelPaymentLabelsCompleted}
        payCounts={hotelPaymentCountsCompleted}
        revenueData={hotelRevenuePerWeek}
        revenueColor={"rgba(99, 102, 241, 0.7)"}
      />

      <footer className="dashboard-footer">
        <small>
          © {new Date().getFullYear()} <strong>སྟབས་བདེ། </strong> Admin • All
          rights reserved
        </small>
      </footer>
    </div>
  );
}
